<template>
  <div class="print-booking-confirm-main">
    <div class="booking-options">
      <div class="invoice-filters-and-table-box-controls">
        <div class="invoice-filters-summary-container">
          <span>{{ filterText }}</span>
        </div>
        <ButtonFilter
          buttonText="Change Filter"
          filterPosition="right"
          :filterWidth="420"
          :isOpen.sync="mainFilterIsOpen"
        >
          <div class="button-filter-content-container">
            <BoxContainer
              class="date-filter-container"
              :title="`Booking Date`"
              :isCheckBox="true"
              :isChecked.sync="dateFilterChecked"
            >
              <DateTimePicker
                :placeholder="`Start`"
                :value.sync="refreshStart"
                :showTime="false"
                :isDisabled="!dateFilterChecked"
              />
              <DateTimePicker
                :placeholder="`End`"
                :value.sync="refreshEnd"
                :showTime="false"
                :isDisabled="!dateFilterChecked"
              />
            </BoxContainer>

            <BoxContainer
              class="booking-no-filter-container"
              title="Search"
              :isChecked.sync="searchFilterChecked"
              :isCheckBox="true"
              style="margin-bottom: 5px"
            >
              <Dropdown
                placeholder="Search By"
                :items="searchFilterItems"
                itemKey="Search Criteria"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :selectedObject.sync="selectedSearchBy"
                :errorMessage.sync="searchByError"
                @selected="searchBySelected"
                validator="text,40"
              />
              <Dropdown
                placeholder="Search Criteria"
                :items="searchCriteriaItems"
                itemKey="name"
                :isDisabled="!searchFilterChecked || criteriaDisabled"
                :isRequired="searchFilterChecked"
                :selectedObject.sync="selectedCriteria"
                validator="text,40"
              />
              <Input
                placeholder="Search"
                :inputValue.sync="searchByValue"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :errorMessage.sync="searchByValueError"
              />
            </BoxContainer>
            <div class="refresh-button-container">
              <button @click="refreshFilterClicked" :disabled="!dateFilterChecked && !searchFilterChecked">
                Refresh
              </button>
            </div>
          </div>
        </ButtonFilter>
      </div>

      <div class="table-box-container">
        <TableBox
          :tableData="printList"
          :title="pageType === 'Freight Booking Confirmation' ? 'Freight Booking' : 'Booking Confirmation'"
          :selectedRow.sync="selectedBooking"
          :loading="tableLoading"
          :selectedIndex="selectedIndex"
        />
      </div>
      <div class="details-block">
        <DateTimePicker
          style="margin-left: 2px; margin-top: 10px"
          placeholder="Date"
          title="Date"
          :value.sync="dateInput"
          :showTime="false"
          :maxDate="smallDateTimeMaxDate"
          :minDate="smallDateTimeMinValue"
          @dateChanged="checkDateTime('reportDate', $event)"
          :errorMessage.sync="errorMessages['reportDate']"
        />
      </div>
      <div class="details-block">
        <Input style="margin-right: 5px" placeholder="To" :inputValue.sync="printDetails['To']" validator="text,40" />
      </div>
      <div class="details-block">
        <Input
          style="margin-right: 5px"
          placeholder="Attn"
          :inputValue.sync="printDetails['Attn1']"
          validator="text,40"
        />
        <Input
          style="margin-right: 5px"
          placeholder="Tel #"
          :inputValue.sync="printDetails['Tel1']"
          validator="text,20"
        />
        <Input
          style="margin-right: 5px"
          placeholder="Fax #"
          :inputValue.sync="printDetails['Fax1']"
          validator="text,20"
        />
      </div>
      <div class="details-block">
        <Input style="margin-right: 5px" placeholder="CC" :inputValue.sync="printDetails['CC']" validator="text,40" />
      </div>
      <div class="details-block">
        <Input
          style="margin-right: 5px"
          placeholder="Attn"
          :inputValue.sync="printDetails['Attn2']"
          validator="text,40"
        />
        <Input
          style="margin-right: 5px"
          placeholder="Tel #"
          :inputValue.sync="printDetails['Tel2']"
          validator="text,20"
        />
        <Input
          style="margin-right: 5px"
          placeholder="Fax #"
          :inputValue.sync="printDetails['Fax2']"
          validator="text,20"
        />
      </div>
      <div class="details-block">
        <BoxContainer title="Footer" style="width: 100%; height: 100%">
          <Dropdown
            style="width: 30%"
            placeholder="Remarks List"
            :items="remarksList"
            itemKey="RemarksCode"
            :selectedObject.sync="selectedRemarks"
            @selected="changeRemarksText"
          />
          <RemarksBox :text.sync="printDetails['RemarksInput']" :maxLength="1000" />
          <RemarksBox title="Disclaimer" :text.sync="printDetails['Disclaimer']" :maxLength="850" />
        </BoxContainer>
      </div>
      <div class="details-block">
        <Input style="width: 30%" placeholder="From" :inputValue.sync="printDetails['Regards']" validator="text,40" />
        <div class="booking-signature-form">
          Signature
          <button @click="editSignature = !editSignature">{{ editSignature ? 'Back' : 'Edit' }}</button>
          <div class="booking-details-remarks">
            <div v-if="!editSignature" style="width: 100%">
              <div v-if="!selectedSignature">No signature selected</div>
              <img v-else :src="selectedSignature" class="selected-signature" />
            </div>
            <div v-else class="signature-another">
              <div v-if="allSignature.length == 0">No available signature</div>
              <div v-else class="choose-signature">
                Choose another signature
                <div v-if="selectedSignature" class="default-signature" style="margin-top: 5px; margin-bottom: 40px">
                  <button @click="defaultSignatureSelect()" style="background-color: red; color: white"
                    >Clear Selected Signature</button
                  >
                </div>
                <div class="default-signature" v-for="(s, i) in allSignature" :key="i">
                  Signature {{ i + 1 }}<button @click="defaultSignatureSelect(i)">Select</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="header-formatting-controls">
        <div class="header-formatting-column">
          <CheckBoxV2 title="Show Company Letter Head" :isChecked.sync="showCompanyLetterHeadIsChecked" />
        </div>
      </div>
      <div class="call-to-action-buttons">
        <button @click="previewClicked">Preview Report</button>
      </div>
    </div>
    <div class="report-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canToggleBackground="false"
        :canShare="true"
        :canClose="false"
        ref="designerV3"
      />
      <div v-else class="user-instructions">
        <span>Select Booking and click on <strong>Preview Report</strong> to get started</span>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  components: {},
  computed: {},
  data: function () {
    return {
      pageType: '',
      jobType: '',
      selectedIndex: [],
      mainFilterIsOpen: false,
      dateFilterChecked: true,
      searchFilterChecked: false,
      refreshStart: new Date(new Date().setDate(new Date().getDate() - 3)),
      refreshEnd: new Date(),
      searchFilterItems: null,
      searchCriteriaItems: [{ name: 'EQUALS' }, { name: 'STARTS WITH' }, { name: 'CONTAINS' }, { name: 'ENDS WITH' }],
      selectedSearchBy: null,
      criteriaDisabled: false,
      selectedCriteria: null,
      searchByValue: '',
      searchByError: '',
      searchByValueError: '',
      filterText: '',

      selectedRemarks: null,
      remarksList: [],

      editSignature: false,
      allSignature: [],
      selectedSignature: null,

      customerAndAgentDetails: null,

      bookingDetails: null,
      printList: [],
      tableLoading: false,
      loginDetails: null,
      printDetails: {
        Date: '',
        To: '',
        Attn1: '',
        Tel1: '',
        Fax1: '',
        CC: '',
        showCC: false,
        Attn2: '',
        Tel2: '',
        Fax2: '',
        RemarksInput: '',
        Regards: '',
        showRegards: false,
        Disclaimer: '',
        showHeader: false,
        _header: '',
        signature: '',
        showSignature: false,
      },
      dateInput: null,
      showCompanyLetterHeadIsChecked: true,

      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: {},
      selectedBooking: [],
      smallDateTimeMinValue: new Date(2000, 0, 1),
      smallDateTimeMaxDate: new Date(2079, 0, 1),
      errorMessages: {
        reportDate: '',
      },
    };
  },
  props: {},
  methods: {
    defaultSignatureSelect: function (index) {
      if (index >= 0) {
        this.selectedSignature = this.allSignature[index];
      } else {
        this.selectedSignature = null;
      }

      this.editSignature = false;
    },
    getSignatureDetails: async function () {
      this.allSignature = [];
      const userOptionsData = await this.$apiCallV2('/UserOptions/GetSettingsSystemUser?settings=UserOptions');
      if (userOptionsData.length > 0) {
        if (userOptionsData[0]['SettingsValue']) {
          const userOptionsObj = JSON.parse(userOptionsData[0]['SettingsValue']);
          if (userOptionsObj['Signature1']) {
            this.allSignature.push(userOptionsObj['Signature1']);
          }
          if (userOptionsObj['Signature2']) {
            this.allSignature.push(userOptionsObj['Signature2']);
          }
          if (userOptionsObj['Signature3']) {
            this.allSignature.push(userOptionsObj['Signature3']);
          }
        }
      }
    },
    refreshFilterClicked: async function () {
      this.printList = [];
      this.selectedBooking = [];

      if (this.dateFilterChecked && this.searchFilterChecked) {
        this.filterText = `Filter by ${this.selectedSearchBy['Search Criteria']} ${this.selectedCriteria['name']} ${this.searchByValue}, from ${new Date(this.refreshStart).formatString('yyyy-MM-dd')} to ${new Date(this.refreshEnd).formatString('yyyy-MM-dd')}`;
      } else if (this.dateFilterChecked && !this.searchFilterChecked) {
        this.filterText = `Filter by date from ${new Date(this.refreshStart).formatString('yyyy-MM-dd')} to ${new Date(this.refreshEnd).formatString('yyyy-MM-dd')}`;
      } else if (!this.dateFilterChecked && this.searchFilterChecked) {
        this.filterText = `Filter by ${this.selectedSearchBy['Search Criteria']} ${this.selectedCriteria['name']} ${this.searchByValue}`;
      }

      const dataToPost = {
        stjobdate: this.dateFilterChecked ? new Date(this.refreshStart).formatString('yyyy-MM-dd') : '',
        edjobdate: this.dateFilterChecked ? new Date(this.refreshEnd).formatString('yyyy-MM-dd') : '',
        jobtype: this.jobType,
        searchfield: this.searchFilterChecked ? this.selectedSearchBy['Search Criteria'] : '',
        searchcriteria: this.searchFilterChecked ? this.selectedCriteria['name'] : '',
        searchvalue: this.searchFilterChecked ? this.searchByValue : '',
      };
      this.tableLoading = true;
      this.printList =
        this.pageType === 'Freight Booking Confirmation' ?
          await this.$apiCallV2(`/FreightBooking/GetFreightBookingListing`, dataToPost)
        : await this.$apiCallV2(`/FreightForwarding/GetListJobTracking`, dataToPost);
      this.tableLoading = false;
      this.mainFilterIsOpen = false;
    },
    searchBySelected: function (e) {
      if (e) {
        if (e['Search Criteria'] == 'Required Date') {
          this.selectedCriteria = this.searchCriteriaItems[0];
          this.criteriaDisabled = true;
        } else {
          this.criteriaDisabled = false;
        }
      }
    },
    previewClicked: function () {
      this.mainFilterIsOpen = false;
      if (this.selectedBooking.length == 0) {
        this.$showAlert('error', 'No Booking Selected', `Please select a record to Preview Booking Confirmation`);
      } else {
        if (this.checkDateTime('reportDate', this.printDetails['Date'])) {
          return;
        }

        const selected =
          this.pageType === 'Freight Booking Confirmation' ?
            this.selectedBooking[0]['Booking No'].trim()
          : this.selectedBooking[0]['Job No'].trim();
        this.previewBookingConfirmationReport(selected);
      }
    },
    refreshListing: async function (selected) {
      let dataToPost = {};
      dataToPost = {
        jobtype: this.jobType,
        searchfield: this.pageType === 'Freight Booking Confirmation' ? 'Booking #' : 'Job #',
        searchcriteria: 'EQUALS',
        searchvalue: selected,
      };

      this.tableLoading = true;
      this.$fullscreenLoading('Getting Booking Details');

      //reset to empty
      this.printList = [];
      this.selectedBooking = [];

      //call api
      this.printList =
        this.pageType === 'Freight Booking Confirmation' ?
          await this.$apiCallV2(`/FreightBooking/GetFreightBookingListing`, dataToPost)
        : await this.$apiCallV2(`/FreightForwarding/GetListJobTracking`, dataToPost);
      this.tableLoading = false;

      if (this.printList.length > 0) {
        // Find the index of the booking that matches `selected`

        const index = this.printList.findIndex(
          (item) =>
            (this.pageType === 'Freight Booking Confirmation' ? item['Booking No'] : item['Job No']) === selected,
        );
        if (index !== -1) {
          this.$nextTick(() => {
            this.selectedBooking = [this.printList[index]]; // Select the record
            this.selectedIndex = [index]; // Using this prop to highlight the row in TableBox, because the above code doesn't work
          });
        }

        if (this.pageType === 'Booking Confirmation') {
          this.customerAndAgentDetails = await this.$apiCallV2(`/PrintNoticeOfArrival/JobCustomerAndAgents`, {
            jobno: selected,
          });
          const jobData = this.customerAndAgentDetails[0];
          this.printDetails['To'] = jobData['SA Name'] ?? '';
          this.printDetails['Attn1'] = jobData['SA Contact'] ?? '';
          this.printDetails['Fax1'] = jobData['SA Fax'] ?? '';
          this.printDetails['Tel1'] = jobData['SA Tel'] ?? '';
          this.printDetails['RemarksInput'] = jobData['Remarks'];
        }
      }

      this.$fullscreenLoading();
    },
    async printFREBookingConfirmationReport(selected) {
      this.bookingDetails = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'FREIGHTBOOKING',
        recordid: selected,
      });

      //wrap object into array
      if (!Array.isArray(this.bookingDetails.Booking)) {
        this.bookingDetails.Booking = [this.bookingDetails.Booking];
      }

      if (this.bookingDetails.Booking[0]['Insurance'] == '1') {
        this.bookingDetails.Booking[0]['Insurance'] = 'Yes';
      } else {
        this.bookingDetails.Booking[0]['Insurance'] = 'No';
      }

      //reformat date
      this.bookingDetails.Booking[0]['POT ETD'] =
        this.bookingDetails.Booking[0]['POT ETD'] ?
          new Date(this.bookingDetails.Booking[0]['POT ETD']).formatString('yyyy-MM-dd')
        : '';
      this.bookingDetails.Booking[0]['Final Destination ETA'] =
        this.bookingDetails.Booking[0]['Final Destination ETA'] ?
          new Date(this.bookingDetails.Booking[0]['Final Destination ETA']).formatString('yyyy-MM-dd')
        : '';

      this.bookingDetails.Booking[0]['POL ETA'] =
        this.bookingDetails.Booking[0]['POL ETA'] ?
          new Date(this.bookingDetails.Booking[0]['POL ETA']).formatString('yyyy-MM-dd')
        : '';
      this.bookingDetails.Booking[0]['POD ETA'] =
        this.bookingDetails.Booking[0]['POD ETA'] ?
          new Date(this.bookingDetails.Booking[0]['POD ETA']).formatString('yyyy-MM-dd')
        : '';

      //format agent address
      if (this.bookingDetails?.Booking?.[0]?.['Oversea Agent']) {
        const agent = this.bookingDetails.Booking[0];

        const addr1 = agent['OA Addr1'] || '';
        const addr2 = agent['OA Addr2'] || '';
        const addr3 = agent['OA Addr3'] || '';
        const addr4 = agent['OA Addr4'] || '';
        const postCode = agent['OA PostCode'] || '';
        const city = agent['OA City'] || '';
        const state = agent['OA State'] || '';

        this.bookingDetails.Booking[0]['OA Address'] =
          `${addr1}<br>${addr2}<br>${addr3}<br>${addr4}<br>${postCode}${postCode ? ', ' : ''}${city}${city ? ', ' : ''}${state}`;
      }

      this.detailDateTime = `${this.listingDAte} ${this.listingTime}`; //Not sure what this is for
    },
    async printBookingConfirmationReport(selected) {
      this.bookingDetails = await this.$apiCallV2(`/Report/GetJobDetails?jobno=${selected}`);
      const jobData = this.bookingDetails['JobDetails']['Job'];
      jobData['Final Dest'] = jobData['Final Dest'] ? new Date(jobData['Final Dest']).formatString('yyyy-MM-dd') : '';

      //Reformat
      jobData['FinalDesc Country'] = `, ${jobData['FinalDesc Country']}`;
      jobData['showWarehouse'] = Boolean(jobData['Warehouse']);
      // Get correct ETA based on job type
      const ETAKey = this.$route.query['jobtype'] === 'I' ? 'POD ETA' : 'POL ETA';
      jobData['ETA Key'] = this.$route.query['jobtype'] === 'I' ? 'ETA Port Of Discharge' : 'ETA Port Of Loading';
      jobData['ETA Value'] = jobData[ETAKey];

      const ETDKey = this.$route.query['jobtype'] === 'I' ? 'POL ETD' : 'POD ETD';
      jobData['ETD Key'] = this.$route.query['jobtype'] === 'I' ? 'ETD Port Of Loading' : 'ETD Port Of Discharge';
      jobData['ETD Value'] = jobData[ETDKey];

      jobData['Volume'] = jobData['Volume'].split(' ')[0];
      jobData['Gross Weight'] = jobData['Gross Weight'].split(' ')[0];

      jobData['NParty Address'] = [
        jobData['NParty Add1'],
        jobData['NParty Add2'],
        jobData['NParty Add3'],
        jobData['NParty Add4'],
        `${jobData['NParty PostCode']} ${jobData['NParty City']}`,
        `${jobData['NParty State']} ${jobData['NParty Country']}`,
      ]
        .filter(Boolean)
        .join('<br>');
    },
    changeRemarksText: function (val) {
      if (val !== null) this.printDetails['RemarksInput'] = val['Remarks'];
    },
    previewBookingConfirmationReport: async function (selected) {
      // this.bookingDetails = null; //reset booking details
      this.$fullscreenLoading('Preparing to Print');
      this.designerV3ReportData = []; //reset report data
      this.printDetails['Date'] = this.dateInput.formatString('yyyy-MM-dd');
      this.designerV3SharedData = this.printDetails;

      if (this.pageType === 'Freight Booking Confirmation') {
        await this.printFREBookingConfirmationReport(selected);
        this.designerV3ReportData = this.bookingDetails.Booking;
        this.designerV3ReportFiles = ['BookingConfirmationFRE.json'];
      } else {
        await this.printBookingConfirmationReport(selected);
        this.designerV3ReportData = [this.bookingDetails['JobDetails']['Job']];
        this.designerV3ReportFiles = ['BookingConfirmation.json'];
      }

      if (
        this.printDetails['CC'] != '' ||
        this.printDetails['Attn2'] != '' ||
        this.printDetails['Tel2'] != '' ||
        this.printDetails['Fax2'] != ''
      ) {
        this.printDetails.showCC = true;
      } else {
        this.printDetails.showCC = false;
      }
      if (this.printDetails['Regards'] != '') {
        this.printDetails.showRegards = true;
      } else {
        this.printDetails.showRegards = false;
      }

      if (this.designerV3ReportData[0]['Cargo Type'] == 'FCL') {
        this.designerV3ReportData[0].isFCL = true;
      } else {
        this.designerV3ReportData[0].isFCL = false;
      }

      //reformat package details (TBA if empty/0)
      if (this.designerV3ReportData[0]['Package Code'] === 'TBA' || !this.designerV3ReportData[0]['Package Code']) {
        this.designerV3ReportData[0]['Package Desc'] = 'TBA';
      }
      const volume = Number(this.designerV3ReportData[0]['Volume']);
      if (!Number.isFinite(volume) || volume === 0) {
        this.designerV3ReportData[0]['Volume'] = 'TBA';
      } else {
        this.designerV3ReportData[0]['Volume'] = `${this.designerV3ReportData[0]['Volume']} M3`;
      }
      const grossWeight = Number(this.designerV3ReportData[0]['Gross Weight']);
      if (!Number.isFinite(grossWeight) || grossWeight === 0) {
        this.designerV3ReportData[0]['Gross Weight'] = 'TBA';
      } else {
        this.designerV3ReportData[0]['Gross Weight'] = `${this.designerV3ReportData[0]['Gross Weight']} KGS`;
      }

      if (this.designerV3ReportData[0].Transshipment == '1') {
        this.designerV3ReportData[0].showTranshipment = true;
      } else {
        this.designerV3ReportData[0].showTranshipment = false;
      }

      if (this.designerV3ReportData[0]['GoodsDesc'] && this.designerV3ReportData[0]['GoodsDesc'].length > 0) {
        this.designerV3ReportData[0]['Goods Description'] = this.designerV3ReportData[0]['GoodsDesc']
          .map((item) => item.GoodsDescription)
          .join('<br>');
      }

      //set company letter head
      if (this.showCompanyLetterHeadIsChecked == true) {
        this.printDetails.showHeader = true;
        this.printDetails._header = `https://dsmapi.logistics.systems/logo/CoHeaderOpr_${this.loginDetails.CoRefname}.png`;
      } else {
        this.printDetails.showHeader = false;
        this.printDetails._header = '';
      }

      if (this.selectedSignature) {
        this.printDetails.showSignature = true;
        this.printDetails.signature = this.selectedSignature;
      } else {
        this.printDetails.showSignature = false;
        this.printDetails.signature = '';
      }

      this.showDesignerV3 = true;

      this.$fullscreenLoading();
    },
    checkDateTime: function (field, date) {
      if (date != null && date != '') {
        if (date > this.smallDateTimeMaxDate) {
          const errMsg = `Please select date earlier than ${this.smallDateTimeMaxDate.formatString('yyyy-MM-dd')}`;
          if (field == 'reportDate') {
            this.errorMessages['reportDate'] = errMsg;
          }
          return true;
        } else if (date < this.smallDateTimeMinValue) {
          const errMsg = `Please select date later than ${this.smallDateTimeMinValue.formatString('yyyy-MM-dd')}`;
          if (field == 'reportDate') {
            this.errorMessages['reportDate'] = errMsg;
          }
          return true;
        }
      }
    },
    async routingInitialise() {
      const selectedSearchByPageType = {
        'Freight Booking Confirmation': [
          { 'Search Criteria': 'Booking #', '#': 1 },
          { 'Search Criteria': 'Job #', '#': 2 },
          { 'Search Criteria': 'BL No #', '#': 3 },
          { 'Search Criteria': 'Cargo Type', '#': 4 },
          { 'Search Criteria': 'Shipper/Consignee', '#': 5 },
          { 'Search Criteria': 'Billing Customer', '#': 6 },
          { 'Search Criteria': 'Project #', '#': 7 },
          { 'Search Criteria': 'Container #', '#': 8 },
          { 'Search Criteria': 'Transport Mode', '#': 9 },
          { 'Search Criteria': 'Customer Ref #', '#': 10 },
          { 'Search Criteria': 'Vessel Name', '#': 11 },
          { 'Search Criteria': 'Mother Vessel Name', '#': 12 },
          { 'Search Criteria': 'Vessel Booking Ref #', '#': 13 },
          { 'Search Criteria': 'User ID', '#': 14 },
          { 'Search Criteria': 'Container Type', '#': 15 },
          { 'Search Criteria': 'Goods Description', '#': 16 },
        ],
        'Booking Confirmation': await this.$apiCallV2('/Master/GetListMaster', {
          masterfile: 'jobquery',
          module: 'FMS',
        }),
      };
      this.searchFilterItems = selectedSearchByPageType[this.pageType];
      const { bookingno, jobno, jobtype } = this.$route.query; // There should not be a query where a url has both bookingno + jobno
      const searchValue = bookingno || jobno;
      this.jobType = jobtype;
      if (searchValue) {
        this.dateFilterChecked = false;
        this.searchFilterChecked = true;
        this.searchByValue = searchValue;
        this.refreshListing(searchValue);
      } else {
        this.refreshFilterClicked();
      }

      if (!this.selectedSearchBy) {
        this.selectedSearchBy = this.searchFilterItems[0];
      }

      if (!this.selectedCriteria) {
        this.selectedCriteria = this.searchCriteriaItems[2];
      }
    },
  },
  async mounted() {
    const pageRoute = this.$route.path.split('/');
    this.$fullscreenLoading('Getting Booking Details');
    await this.getSignatureDetails();
    const newPageType = pageRoute[pageRoute.length - 1].split('?')[0];
    this.pageType = newPageType;
    this.dateInput = new Date();
    const defaultBookingConfirmationLetter = [
      {
        RemarksCode: 'FCL01',
        Remarks: 'Empty Pick Up AT :-',
      },
      {
        RemarksCode: 'FCL02',
        Remarks: 'Full Return To :',
      },
      {
        RemarksCode: 'FCL03',
        Remarks:
          'If Request For Purchase Marine Insurance, Foam D / Certificate Origin, Pls Kindly Mention And Chop Sign By Return At This Booking Confirmation.',
      },
      {
        RemarksCode: 'FCL04',
        Remarks: 'Booking As Per Email _____ And _____',
      },
      {
        RemarksCode: 'FCL05',
        Remarks: 'Will Arrange Lorry Transport To Pick Up The Cargo At Your Premise On __/__/____',
      },
      {
        RemarksCode: 'LCL01',
        Remarks:
          'If Request For Purchase Marine Insurance, Foam D / Certificate Origin, Pls Kindly Mention And Chop Sign By Return At This Booking Confirmation.',
      },
      {
        RemarksCode: 'LCL02',
        Remarks: 'Will Arrange Lorry Transport To Pick Up The Cargo At Your Premise On __/__/____',
      },
      {
        RemarksCode: 'LCL03',
        Remarks: 'Booking As Per Email _____ And _____',
      },
      {
        RemarksCode: 'SI001',
        Remarks:
          'PLEASE FAX THE DRAFT BL FOR OUR CONFIRMATION.PLS APPLY FOR 14 DAYS FREE DETENTION AND DEMURRAGE AT DESTINATION.',
      },
    ];

    const defaultFREBookingConfirmationLetter = [
      {
        RemarksCode: 'OBLINV',
        Remarks:
          'Please be informed that we have yet to receive the                                                            .  As such, we will not be able to clear the shipment.  For your information the last day of free storage/demurrage is on                           and the fees incurred will be charged to you accordingly.',
      },
      {
        RemarksCode: 'NSPACE',
        Remarks:
          'We are unable to deliver the laden container  due to limited space /congestion at your warehouse. Please note the last day of free storage/demurrage is on                         and the fees incurred  will be charged to you accordingly.  Thus, please advise us once there is space availability for delivery.',
      },
      {
        RemarksCode: 'NDELVR',
        Remarks:
          'As per your request we will not deliver the container untill further instruction from your goodself. Please note the last day of free storage/demurrage is on                    and the fees incurred will be charged to you accordingly.  ',
      },
      {
        RemarksCode: 'CTRDTN',
        Remarks:
          'Please note we/haulier  will not be held responsible for  detention  charge due to                                                                         . The rules and regulations of all haulage companies is that haulier shall be held free from all charges incurred by consignee    within the 48 hours notice period  from receipt of last document or container release',
      },
      {
        RemarksCode: 'LCLUST',
        Remarks:
          'Please be informed the container was  unstuffed on                                           evening and the storage charge will be effective from                                            . The LCL storage fees will be charges to you accordingly.',
      },
      {
        RemarksCode: 'DOCQRY',
        Remarks:
          'Documents submitted and under Customs Query for                                        . Please provide the supporting documents as soon as possible.  Please note the last day of free storage/demurrage is on                    and  we will not be held responsible delays due to this reason.  ',
      },
      {
        RemarksCode: 'NGPASS',
        Remarks:
          'Container advised ready for collection by your goodself but when our haulage / trailer arrived, the Gate Pass was not issued for container release. Please note futile trip fees will apply and bill to you accordingly.',
      },
      {
        RemarksCode: 'CTRUST',
        Remarks:
          'We are unable to collect the empty container because the container is not unstuffed yet. Please note the last day of free detention is on                          and any charges incurred will be billed to you.',
      },
      {
        RemarksCode: 'SSRCHG',
        Remarks:
          'Kindly be informed that container for the subject shipment was laden in CY on                     . Please take note that SSR of RM                being incurred for this shipment due to notification of Laden container less than 24 hours from Port Closing. Laden ready                     .  As such, we will recover the said charges from your office accordingly. Thank you.',
      },
      {
        RemarksCode: 'LadenIn',
        Remarks:
          '   We have already faxed the above laden collection form on ____ time ____. Please arrange to turn in the container to avoid detention charge by agent and acknowledge back by return email. ',
      },
      {
        RemarksCode: 'Mtrelease',
        Remarks:
          'Refer to your request/ booking above, we are unable to deliver the empty container as per request, according to agent the empty container can only release on ___. Attached copy of request remarked by agent/CMO. (choose which applicable) . Therefore, please reconfirm with agent and advise back so that we can arrange accordingly.',
      },
      {
        RemarksCode: 'NoUnit',
        Remarks:
          '      Haulage unable to pick up the empty container  yesterday/ this morning/today  due to no unit. Please check with agent concerned and advise  by return mail for re-arrange. Please note haulage futile trip charge is at RM120.00 per container.',
      },
      {
        RemarksCode: 'DocReq',
        Remarks:
          'Kindly forward the export invoice & packling list asap in order for us to process/submit to customs before closing date.',
      },
      {
        RemarksCode: 'Booking',
        Remarks:
          'Kindly update booking/container no. in the Port system soonest possible to avoid failing in turning in container by haulage. For your information Futile trip charge by haulage is @RM120.00.  The charge will be forward to shipper and in turn shipper will forward to agent.  (if any)',
      },
    ];

    const defaultBookingList =
      this.pageType === 'Freight Booking Confirmation' ?
        defaultFREBookingConfirmationLetter
      : defaultBookingConfirmationLetter;
    const remarksListjson = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'FORWARDING_AGENT' });
    if (!remarksListjson) {
      this.remarksList = defaultBookingList;
    }

    const remarksLabel =
      this.pageType === 'Freight Booking Confirmation' ? 'FREBookingConfirmationLetter' : 'BookingConfirmationLetter';
    const getRemarksDetails = JSON.parse(remarksListjson[0]['addinfojson'])[remarksLabel]?.['Remarks'];
    if (!getRemarksDetails) {
      this.remarksList = defaultBookingList;
    } else {
      this.remarksList = getRemarksDetails;
    }

    const FREDefaultDisclaimer =
      "We hereby confirmed the acceptance of the above booking.\nKindly advise us should you have any changes on shipment details.\n\nThis booking is subject to the following :\n- All dates / times given are estimated and subject to change without prior notice.\n- It is the carrier's right to substitute the named and / or performing vessel with another vessel at any time.\n- All business transactions are subject to standard trading terms and conditions of SFFLA (Selangor Freight Forwarder Logistics\nAssociation. Copy available upon request.";
    const FFDefaultDisclaimer =
      'We hereby confirmed the above mentioned booking.\nKindly advise should you have any changes on shipment details.\n@ The above vessel is subject to delay / change with or without prior notice.\n\nThanks and Best Regards.';
    const getDisclaimer = this.pageType === 'Freight Booking Confirmation' ? 'FRE' : 'FF';
    const fullDisclaimerArray = [];
    for (let i = 1; i <= 4; i++) {
      const disclaimerLine = await this.$apiCallV2(
        `/Master/GetSettingsSystem?settingsitem=${getDisclaimer}_BookConfirm_Dsc${i}`,
      );
      if (disclaimerLine?.length > 0 && disclaimerLine[0]['SettingsValue']) {
        fullDisclaimerArray.push(disclaimerLine[0]['SettingsValue']);
        fullDisclaimerArray.push(' ');
      }
    }

    if (fullDisclaimerArray.length === 0) {
      this.printDetails['Disclaimer'] =
        this.pageType === 'Freight Booking Confirmation' ? FREDefaultDisclaimer : FFDefaultDisclaimer;
    } else {
      const fullDisclaimer = fullDisclaimerArray.join('').replaceAll('\\n', '\n');
      this.printDetails['Disclaimer'] = fullDisclaimer;
    }

    this.$fullscreenLoading('Getting Login Details');
    this.loginDetails = await this.$apiCallV2('/Login/GetDetails');
    this.$fullscreenLoading();

    await this.routingInitialise();
    this.$fullscreenLoading();
  },
  watch: {
    async selectedBooking(_, newValue) {
      if (newValue && this.pageType === 'Booking Confirmation') {
        const selected = newValue[0]?.['Job No'];
        if (selected) {
          const oldSelected = this.customerAndAgentDetails;
          const oldRemarks = oldSelected['Remarks']?.trim();
          this.customerAndAgentDetails = await this.$apiCallV2(`/PrintNoticeOfArrival/JobCustomerAndAgents`, {
            jobno: selected,
          });
          const jobData = this.customerAndAgentDetails[0];
          this.printDetails['To'] = jobData['SA Name'] ?? '';
          this.printDetails['Attn1'] = jobData['SA Contact'] ?? '';
          this.printDetails['Fax1'] = jobData['SA Fax'] ?? '';
          this.printDetails['Tel1'] = jobData['SA Tel'] ?? '';
          this.printDetails['RemarksInput'] = this.printDetails['RemarksInput'] ?? ''; // If field becomes empty, it turns to null which messes up the trimming
          if (
            this.printDetails['RemarksInput'].trim() === '' ||
            this.printDetails['RemarksInput'].trim() === oldRemarks ||
            this.printDetails['RemarksInput'].trim() === this.selectedRemarks?.['Remarks']?.trim()
          ) {
            this.printDetails['RemarksInput'] = jobData['Remarks'];
            this.selectedRemarks = null;
          }
        }
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/css/all.scss';

.print-booking-confirm-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;
  overflow: hidden;

  > .booking-options {
    margin-right: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;
    width: 50%;
    overflow-y: auto;
    height: 100%;

    > .table-box-container {
      height: 40%;
      // height: 50%; // ============ change later
    }

    > .details-block {
      display: flex;
      width: 100%;
      position: relative;
      margin-bottom: 2px;
      justify-content: space-between;

      > .details-column {
        margin-right: 5px;
      }
    }

    .booking-signature-form {
      width: 50%;
      padding-top: 2px button {
        padding: 0 15px;
        float: right;
      }

      .booking-details-remarks {
        border: 1px solid rgb(200, 200, 200);
        border-radius: 3px;
        overflow: auto;
        display: flex;
        flex-wrap: wrap;
        padding-left: 5px;
        padding-right: 5px;
        min-height: 100px;

        .signature-another {
          width: 100%;

          .choose-signature {
            .default-signature {
              margin-top: 8px;

              button {
                float: right;
                padding: 2px 5px;
              }
            }
          }
        }
      }
    }

    .selected-signature {
      height: 100%;
      width: 100%;
      object-fit: contain;
    }

    > .call-to-action-buttons {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }

    > .invoice-filters-and-table-box-controls {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-bottom: 5px;

      > .invoice-filters-summary-container {
        margin-right: 5px;
        align-content: center; // vertically align the summary text
        text-align: end; // horizontally align the summary text
      }

      .button-filter-content-container {
        text-align: left;
        cursor: auto;

        .invoice-no-filter-container {
          margin-top: 15px;
        }

        .customer-filter-container {
          margin-top: 15px;
        }

        .outstanding-and-job-type-filters {
          display: flex;
          flex-direction: row;

          > .outstanding-dropdown {
            margin-right: 5px;
          }

          > .job-type-dropdown {
            margin-left: 5px;
          }
        }

        .not-printed-filter {
          margin-top: 5px;
          display: flex;
          justify-content: flex-start;
        }

        .refresh-button-container {
          margin-top: 10px;
          display: flex;
          justify-content: flex-end;
        }
      }

      > .select-deselect-all-button {
        margin-left: 5px;
      }
    }
  }

  > .report-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .user-instructions {
      height: 100%;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }
  }
}
</style>
